import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyMeetAddRequestDraft,
    clearMeetAddRequestDraft,
    createEditMeetAddRequestDraftKey,
    createMeetAddRequestDraftPayload,
    readMeetAddRequestDraft,
    saveMeetAddRequestDraft,
} from './meetAddRequestDraftUtils'

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultParams = (meetAddRequestId = null) => ({
    meetAddRequestId,
    companyName: '',
    phone: '',
    email: '',
    homepage: '',
    area: '',
    startTime: '',
    endTime: '',
    beginTime: '',
    finishTime: '',
    title: '',
    content: '',
    status: 1,
})

export default function MeetAddRequest(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.meetAddRequestId
    const listCurPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect
    const listSearch = location.search || ''
    const draftKey = createEditMeetAddRequestDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState(() => createDefaultParams(id))
    const [username, setUsername] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const initialDraftSnapshotRef = useRef('')

    const resetDetailState = useCallback((errorMessage) => {
        setParams(createDefaultParams(id))
        setUsername('')
        setHasDetail(false)
        setDraftReady(false)
        setDetailErrorMessage(errorMessage)
    }, [id])
    
    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 대회 등록 문의 상세를 불러올 수 없습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/meetAddRequest/getDetail', { meetAddRequestId: id })
                if (data.code === 200) {
                    const baseParams = data.data || {}

                    if (!baseParams.meetAddRequestId) {
                        resetDetailState('대회 등록 문의 상세 정보를 찾을 수 없습니다.')
                        return
                    }

                    const storedDraft = readMeetAddRequestDraft(draftKey)
                    const restoredDraft = applyMeetAddRequestDraft({
                        baseParams,
                        draft: storedDraft,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createMeetAddRequestDraftPayload({
                        params: baseParams,
                    }))

                    setParams(restoredDraft.params)
                    setUsername(baseParams.user?.username || '')
                    setHasDetail(Boolean(baseParams?.meetAddRequestId))
                    setDetailErrorMessage('')
                    setDraftReady(true)
                } else if (data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || '대회 등록 문의 상세를 불러오지 못했습니다.'
                    resetDetailState(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = '대회 등록 문의 상세를 불러오지 못했습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setLoading(false)
            }
        }
        getData()
    }, [draftKey, history, id, resetDetailState])

    useEffect(() => {
        if (!draftReady || !hasDetail || !params?.meetAddRequestId) {
            return
        }

        // 대회 등록 문의 상세는 목록 query를 건드리지 않고 상태 draft만 sessionStorage에 보존한다.
        const nextDraft = createMeetAddRequestDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearMeetAddRequestDraft(draftKey)
            return
        }

        saveMeetAddRequestDraft(draftKey, nextDraft)
    }, [draftKey, draftReady, hasDetail, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        // 저장 후에도 운영자가 보던 대회 등록 문의 목록 조건으로 그대로 복귀시킨다.
        clearMeetAddRequestDraft(draftKey)
        history.push(createListLinkTarget({
            pathname: '/meetAddRequests',
            search: listSearch,
            searchText: searchText,
            searchSelect: searchSelect,
            curPage: listCurPage,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        }))
    }

    const handleUpdateField = (e) => {
        setParams({
            ...params,
            [e.target.name]: e.target.value
        })
    }

    const updateData = async () => {
        if (saving) {
            return
        }

        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/meetAddRequest/update', { meetAddRequestId: params.meetAddRequestId, status: params.status })

            if (data.code === 200) {
                moveToList({ severity: 'success', message: '대회 등록 문의 상태를 저장했습니다.' })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('대회 등록 문의 상태를 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        updateData()
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="대회 등록 문의 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/meetAddRequests" search={listSearch} visible={false} searchText={searchText} searchSelect={searchSelect} curPage={listCurPage} onBackClick={() => clearMeetAddRequestDraft(draftKey)} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>대회 등록 문의 상세</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>대회 등록 문의 상세</Title>
                <Box component="form" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }} >
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label="상태"
                                    select
                                    id="status"
                                    name="status"
                                    value={params.status}
                                    onChange={handleUpdateField}
                                    size="small"
                                    sx={{ minWidth: 150 }}
                                    disabled={saving}
                                >
                                    <MenuItem value={1}>신청</MenuItem>
                                    <MenuItem value={2}>검토중</MenuItem>
                                    <MenuItem value={3}>등록완료</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    label="이름"
                                    id="username"
                                    name="username"
                                    value={username || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    InputProps={{ readOnly: true, }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    label="회사명"
                                    id="companyName"
                                    name="companyName"
                                    value={params.companyName || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    InputProps={{ readOnly: true, }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    label="연락처"
                                    id="phone"
                                    name="phone"
                                    value={params.phone || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    InputProps={{ readOnly: true, }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    label="이메일"
                                    id="email"
                                    name="email"
                                    value={params.email || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    InputProps={{ readOnly: true, }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <FormControl fullWidth>
                                <TextField
                                    label="홈페이지"
                                    id="homepage"
                                    name="homepage"
                                    value={params.homepage || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    InputProps={{ readOnly: true, }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <FormControl fullWidth>
                                <TextField
                                    label="지역"
                                    id="area"
                                    name="area"
                                    value={params.area || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    InputProps={{ readOnly: true, }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    label="접수 시작일"
                                    id="startTime"
                                    name="startTime"
                                    value={params.startTime || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    InputProps={{ readOnly: true, }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    label="접수 종료일"
                                    id="endTime"
                                    name="endTime"
                                    value={params.endTime || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    InputProps={{ readOnly: true, }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    label="대회 시작일"
                                    id="beginTime"
                                    name="beginTime"
                                    value={params.beginTime || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    InputProps={{ readOnly: true, }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    label="대회 종료일"
                                    id="finishTime"
                                    name="finishTime"
                                    value={params.finishTime || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    InputProps={{ readOnly: true, }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label="제목"
                                    id="title"
                                    name="title"
                                    multiline
                                    rows={4}
                                    value={params.title || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    InputProps={{ readOnly: true, }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label="내용"
                                    id="content"
                                    name="content"
                                    multiline
                                    rows={8}
                                    value={params.content || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    InputProps={{ readOnly: true, }}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        visible={true}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        backDisabled={saving}
                        onBackClick={() => moveToList()}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
